import type { RemoteQuizConfig } from '@/modules/quiz/onboardingSettings';

/** Manual check against WebQuiz `firebaseConfigSchema` (no ajv); returns a copy with known fields only. */
export interface ValidationResult {
  valid: boolean;
  config: RemoteQuizConfig | null;
  errors: string[];
}

const ONBOARDING_OPTIONAL = ['promoDiscount'] as const;
const PAYWALL_OPTIONAL = [
  'specialOfferModal',
  'linkParams',
  'returnPopupSlug',
  'scratchRedirectPaywall',
  'scratchDiscountPercent',
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const pickStrings = <K extends string>(
  source: Record<string, unknown>,
  path: string,
  required: readonly K[],
  optional: readonly K[],
  errors: string[],
): Record<K, string> => {
  const result = {} as Record<K, string>;
  for (const key of required) {
    const value = source[key];
    if (typeof value !== 'string') errors.push(`${path}.${key}: expected string`);
    else result[key] = value;
  }
  for (const key of optional) {
    const value = source[key];
    if (value === undefined) continue;
    if (typeof value !== 'string') errors.push(`${path}.${key}: expected string`);
    else result[key] = value;
  }
  return result;
};

export const validateRemoteQuizConfig = (input: unknown): ValidationResult => {
  const errors: string[] = [];
  if (!isRecord(input)) return { valid: false, config: null, errors: ['config: expected object'] };

  const onboarding = input.onboardingProperties;
  const paywall = input.paywallProperties;
  if (!isRecord(onboarding)) errors.push('onboardingProperties: expected object');
  if (!isRecord(paywall)) errors.push('paywallProperties: expected object');
  if (errors.length) return { valid: false, config: null, errors };

  const config: RemoteQuizConfig = {
    onboardingProperties: pickStrings(
      onboarding as Record<string, unknown>,
      'onboardingProperties',
      ['variant'],
      ONBOARDING_OPTIONAL,
      errors,
    ),
    paywallProperties: pickStrings(
      paywall as Record<string, unknown>,
      'paywallProperties',
      ['variant'],
      PAYWALL_OPTIONAL,
      errors,
    ),
  };
  return errors.length ? { valid: false, config: null, errors } : { valid: true, config, errors };
};
